import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import AuthCard from '../components/AuthCard';
import Button from '../components/Button';
import Field from '../components/Field';
import FormBanner from '../components/FormBanner';
import { errorMessage } from '../lib/errors';
import { EMAIL_MAX_LENGTH, emailError } from '../lib/validation';

export default function ForgotPage() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const eErr = emailError(email);
    if (eErr) {
      setFieldError(eErr);
      return;
    }
    setFieldError(undefined);
    setFormError(null);
    setSubmitting(true);
    try {
      // Enumeration-safe: the response never reveals whether the email exists.
      await api.forgotPassword({ email: email.trim() });
      setDone(true);
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Reset your password"
      subtitle={done ? undefined : 'Enter your email and we’ll send a reset link.'}
      footer={
        <Link to="/login" className="text-[var(--cad-accent,#818cf8)] hover:text-[var(--cad-accent,#a5b4fc)]">
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <FormBanner kind="success">
          If an account exists for that email, a reset link is on its way.
        </FormBanner>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          {formError ? <FormBanner kind="error">{formError}</FormBanner> : null}
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            maxLength={EMAIL_MAX_LENGTH}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldError}
          />
          <Button type="submit" loading={submitting} className="w-full">
            Send reset link
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
